// Global Instructions Rule Applied!
import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Avatar, Card, Descriptions, List, Spin, Typography } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight, faChevronDown } from '@fortawesome/free-solid-svg-icons'
import {
  fetchCompanyById,
  fetchAccountInvestmentMeasures
} from '../../../../app_modules/companies/services/companiesService.ts'
import { fetchAccountCommitments } from '../../../../app_modules/companies/services/commitmentsService.ts'
import { groupCommitmentsByFund, type FundGroupRow } from '../../../../app_modules/companies/utils/groupCommitmentsByFund.ts'
import { accountHasPortfolioRole } from '../../../../app_modules/companies/utils/accountRoles.ts'
import { selectCompanyById } from '../../../../state_manager/state_update_actions/companies/selectors.ts'
import { fetchTasksForAccount } from '../../../../app_modules/tasks/services/tasksService.ts'
import { fetchInteractionsForAccount } from '../../../../app_modules/interactions/services/interactionsService.ts'
import { useLookups } from '../../../../app_modules/global_configs/hooks/useLookups.ts'
import { getAccessToken } from '../../../auth/authService'
import { store } from '../../../../state_manager/store.ts'
import { useRecordDetail } from '../lib/useRecordDetail.ts'
import { useRecordRecent } from '../lib/useRecordRecent.ts'
import { useSectionData } from '../lib/useSectionData.ts'
import { detailPath, avatarInitials } from '../lib/routes.ts'
import { text, num, personName, externalHref, dateText } from '../lib/format.ts'
import { compactMoneyByCode } from '../lib/money.ts'
import { LoadingState, ErrorState, Section, SectionSpinner, SectionError } from '../components/ui.tsx'
import { TaskList } from '../components/TaskList.tsx'
import { InteractionList } from '../components/InteractionList.tsx'

/**
 * Company detail (read-only). Reuses `fetchCompanyById` (slice-first via useRecordDetail)
 * for the record and `fetchAccountInvestmentMeasures` for per-fund holdings, plus lazy
 * per-section fetches for tasks (`fetchTasksForAccount`, with embedded `prog__tasks` as
 * first paint) and recent activity (`fetchInteractionsForAccount`). Funding rounds come
 * embedded on the record (`prog__rounds`). Cross-links to funds (holdings) and contacts
 * (team).
 */
export function CompanyScreen(): React.JSX.Element {
  const { id } = useParams()
  const navigate = useNavigate()
  const { record: company, loading, error } = useRecordDetail<any>(id, selectCompanyById, fetchCompanyById)
  useRecordRecent('companies', id, company?.accounts__name, company?.accounts__industry__label)

  // Per-fund investment measures aren't on the record — fetch them alongside (component-local,
  // same as the desktop InvestmentSummarySection).
  const [measures, setMeasures] = useState<any[] | null>(null)
  useEffect(() => {
    if (!id) return
    let cancelled = false
    const token = getAccessToken(store.getState().auth)
    fetchAccountInvestmentMeasures(id, token)
      .then((res) => {
        if (!cancelled) setMeasures(Array.isArray(res.data) ? res.data : [])
      })
      .catch(() => {
        if (!cancelled) setMeasures([])
      })
    return () => {
      cancelled = true
    }
  }, [id])

  // Lookup labels (e.g. funding stage) resolve from the already-loaded globalConfigs slice.
  const { getLookupLabel } = useLookups()
  const currencies = useSelector((s: any) => s?.globalConfigs?.currencies)
  const tasksState = useSectionData<any[]>(id, (key, token) => fetchTasksForAccount(key, { include_completed: true }, token))
  const activityState = useSectionData<any[]>(id, (key, token) => fetchInteractionsForAccount(key, undefined, token))

  // Commitments are investor-account data — fetch only when the record reports
  // commitments (prog__commitments_count), gating like the desktop Commitments tab.
  const commitmentsEnabled = Number((company as any)?.prog__commitments_count ?? 0) > 0
  const commitmentsState = useSectionData<Record<string, any>[]>(
    id,
    async (key, token) => {
      const res = await fetchAccountCommitments(key, { pageSize: 1000 }, token)
      if (res.error) throw new Error(res.error)
      return res.data
    },
    commitmentsEnabled
  )
  const [expandedFunds, setExpandedFunds] = useState<Record<string, boolean>>({})

  if (loading && !company) return <LoadingState label="Loading company…" />
  if (!company) return <ErrorState message={error} />

  const stats = company.prog__investment_stats || null
  const recentRound = company.prog__recent_round || company.prog__latest_round || null
  // The embedded round objects carry the funding-stage lookup id, not its label.
  const recentRoundStage =
    recentRound?.rounds__funding_stage__label ||
    (recentRound?.rounds__funding_stage ? getLookupLabel(String(recentRound.rounds__funding_stage)) : '')
  const teamRaw: any[] = Array.isArray(company.prog__company_team) ? company.prog__company_team : []
  // prog__company_team carries one entry per contact_accounts junction row, so a
  // contact holding several live relationships on the account (e.g. a Founder row
  // plus an Employee row — a supported pairing) appears more than once. Collapse
  // to one row per person, or the List below renders duplicate contacts__id keys.
  const teamByContact = new Map<string, any>()
  for (const row of teamRaw) {
    const contactKey = String(row.contacts__id ?? row.contact_accounts__id)
    const existing = teamByContact.get(contactKey)
    if (!existing) {
      teamByContact.set(contactKey, { ...row })
    } else {
      if (row.prog__founder) existing.prog__founder = true
      if (!existing.contact_accounts__title && row.contact_accounts__title) {
        existing.contact_accounts__title = row.contact_accounts__title
      }
      if (!existing.contacts__photo_url && row.contacts__photo_url) {
        existing.contacts__photo_url = row.contacts__photo_url
      }
    }
  }
  const team = Array.from(teamByContact.values())
  // The measures RPC includes a 'Total' rollup row — drop it from the per-fund list.
  const fundHoldings = (measures || []).filter((m) => String(m?.funds__name ?? '').toLowerCase() !== 'total')
  const fallbackTasks: any[] = Array.isArray(company.prog__tasks) ? company.prog__tasks : []
  const taskRows: any[] = tasksState.data ?? fallbackTasks
  const rounds: any[] = Array.isArray(company.prog__rounds) ? company.prog__rounds : []
  // Display newest-first; ISO dates compare lexicographically (undated rounds sink).
  const sortedRounds = [...rounds].sort((a, b) =>
    String(b?.rounds__round_close_date || '').localeCompare(String(a?.rounds__round_close_date || ''))
  )

  // Portfolio-only sections (Investment summary, Valuation, Holdings) — hidden
  // for accounts without a Portfolio Company / Exited / Dead role (matches desktop).
  const showPortfolioSections = accountHasPortfolioRole(company)
  // Commitments summary: flat rows grouped by fund, with the per-currency grand total.
  const commitmentRows = commitmentsState.data ?? []
  const fundGroups = groupCommitmentsByFund(commitmentRows)
  const commitmentTotalRows = commitmentRows.filter((r: any) => r._total_label)
  const commitmentsGrandTotal = commitmentTotalRows.length
    ? commitmentTotalRows
        .map((t: any) => compactMoneyByCode(t.fund_commitments__amount, t.currency_code, currencies))
        .join('  ·  ')
    : null

  return (
    <div style={{ padding: 12 }}>
      <Card size="small" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar size={44} style={{ backgroundColor: '#1890ff', color: '#fff' }}>
            {avatarInitials(company.accounts__name)}
          </Avatar>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>{text(company.accounts__name)}</div>
            {company.accounts__industry__label ? (
              <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 13 }}>{text(company.accounts__industry__label)}</div>
            ) : null}
          </div>
        </div>
        {company.accounts__website ? (
          <div style={{ marginTop: 10, wordBreak: 'break-all' }}>
            <a href={externalHref(company.accounts__website)} target="_blank" rel="noreferrer">
              {text(company.accounts__website)}
            </a>
          </div>
        ) : null}
      </Card>

      {showPortfolioSections && stats ? (
        <Section title="Investment summary">
          <Descriptions column={1} size="small" colon={false} labelStyle={{ width: '45%' }}>
            <Descriptions.Item label="Invested">{text(stats.invested_fmt)}</Descriptions.Item>
            <Descriptions.Item label="Cost basis">{text(stats.cost_basis_fmt)}</Descriptions.Item>
            <Descriptions.Item label="Realized">{text(stats.realized_value_fmt)}</Descriptions.Item>
            <Descriptions.Item label="Unrealized">{text(stats.unrealized_value_fmt)}</Descriptions.Item>
            <Descriptions.Item label="MOIC">{text(stats.moic_fmt)}</Descriptions.Item>
          </Descriptions>
        </Section>
      ) : null}

      {showPortfolioSections && (recentRound || company.prog__valuation != null) ? (
        <Section title="Valuation">
          <Descriptions column={1} size="small" colon={false} labelStyle={{ width: '45%' }}>
            {company.prog__valuation != null ? (
              <Descriptions.Item label="Valuation">{num(company.prog__valuation)}</Descriptions.Item>
            ) : null}
            {recentRound?.rounds__name ? (
              <Descriptions.Item label="Latest round">{text(recentRound.rounds__name)}</Descriptions.Item>
            ) : null}
            {recentRoundStage ? <Descriptions.Item label="Stage">{text(recentRoundStage)}</Descriptions.Item> : null}
            {recentRound?.rounds__pre_money_valuation != null ? (
              <Descriptions.Item label="Pre-money">{num(recentRound.rounds__pre_money_valuation)}</Descriptions.Item>
            ) : null}
            {recentRound?.rounds__post_money_valuation != null ? (
              <Descriptions.Item label="Post-money">{num(recentRound.rounds__post_money_valuation)}</Descriptions.Item>
            ) : null}
            {recentRound?.rounds__round_close_date ? (
              <Descriptions.Item label="Closed">{text(recentRound.rounds__round_close_date)}</Descriptions.Item>
            ) : null}
          </Descriptions>
        </Section>
      ) : null}

      {showPortfolioSections && sortedRounds.length > 0 ? (
        <Section title="Funding rounds">
          <List
            size="small"
            dataSource={sortedRounds}
            rowKey={(r: any) => String(r.rounds__id)}
            renderItem={(r: any) => {
              const stage = r.rounds__funding_stage ? getLookupLabel(String(r.rounds__funding_stage)) : ''
              const valuation =
                r.prog__valuation != null ? `${num(r.prog__valuation)} ${r.prog__valuation_type === 'PRE' ? 'Pre' : 'Post'}` : null
              const meta = [
                stage || null,
                r.rounds__raise_amount != null ? `Raised ${num(r.rounds__raise_amount)}` : null,
                valuation,
                r.rounds__round_close_date ? dateText(r.rounds__round_close_date) : null
              ]
                .filter(Boolean)
                .join('  ·  ')
              return (
                <List.Item style={{ paddingLeft: 4, paddingRight: 4 }}>
                  <List.Item.Meta title={text(r.rounds__name)} description={meta || undefined} />
                </List.Item>
              )
            }}
          />
        </Section>
      ) : null}

      {showPortfolioSections ? (
        <Section title="Holdings by fund">
        {measures === null ? (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <Spin size="small" />
          </div>
        ) : fundHoldings.length === 0 ? (
          <Typography.Text type="secondary">No fund holdings.</Typography.Text>
        ) : (
          <List
            size="small"
            dataSource={fundHoldings}
            rowKey={(m: any) => String(m.funds__id ?? m.funds__name)}
            renderItem={(m: any) => {
              const metrics = [
                m.cost_fmt ? `Cost ${m.cost_fmt}` : null,
                m.fmv_fmt ? `Value ${m.fmv_fmt}` : null,
                m.moic_fmt ? `MOIC ${m.moic_fmt}` : null
              ]
                .filter(Boolean)
                .join('  ·  ')
              const clickable = Boolean(m.funds__id)
              return (
                <List.Item
                  onClick={clickable ? () => navigate(detailPath('funds', m.funds__id)) : undefined}
                  style={{ cursor: clickable ? 'pointer' : 'default', paddingLeft: 4, paddingRight: 4 }}
                >
                  <List.Item.Meta title={text(m.funds__name)} description={metrics || undefined} />
                  {clickable ? <FontAwesomeIcon icon={faChevronRight} style={{ color: 'rgba(0,0,0,0.25)' }} /> : null}
                </List.Item>
              )
            }}
          />
        )}
        </Section>
      ) : null}

      {commitmentsEnabled ? (
        commitmentsState.data === null && commitmentsState.loading ? (
          <Section title="Commitments">
            <SectionSpinner />
          </Section>
        ) : commitmentsState.data === null && commitmentsState.error ? (
          <Section title="Commitments">
            <SectionError message="Couldn't load commitments." onRetry={commitmentsState.retry} />
          </Section>
        ) : fundGroups.length > 0 ? (
          <Section
            title="Commitments"
            extra={
              commitmentsGrandTotal ? <Typography.Text type="secondary">{commitmentsGrandTotal}</Typography.Text> : undefined
            }
          >
            <List
              size="small"
              dataSource={fundGroups}
              rowKey={(g: FundGroupRow) => g.key}
              renderItem={(g: FundGroupRow) => {
                const isMulti = g.kind === 'fund'
                const expanded = !!expandedFunds[g.key]
                const totalCompact = compactMoneyByCode(g.fund_total_amount, g.currency_code, currencies)
                const subtitle = isMulti ? g.entity_label : text(g.investors__name)
                const onClick = isMulti
                  ? () => setExpandedFunds((m) => ({ ...m, [g.key]: !m[g.key] }))
                  : g.funds__id
                    ? () => navigate(detailPath('funds', g.funds__id as string))
                    : undefined
                return (
                  <List.Item
                    style={{ display: 'block', paddingLeft: 4, paddingRight: 4, cursor: onClick ? 'pointer' : 'default' }}
                    onClick={onClick}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 14 }}>{text(g.funds__name)}</div>
                        {subtitle ? <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>{subtitle}</div> : null}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <span style={{ fontWeight: 500 }}>{totalCompact}</span>
                        {isMulti ? (
                          <FontAwesomeIcon
                            icon={expanded ? faChevronDown : faChevronRight}
                            style={{ color: 'rgba(0,0,0,0.25)' }}
                          />
                        ) : g.funds__id ? (
                          <FontAwesomeIcon icon={faChevronRight} style={{ color: 'rgba(0,0,0,0.25)' }} />
                        ) : null}
                      </div>
                    </div>
                    {isMulti && expanded ? (
                      <div style={{ marginTop: 8, paddingLeft: 8, borderLeft: '2px solid #f0f0f0' }}>
                        {(g.children || []).map((c: FundGroupRow) => (
                          <div
                            key={c.key}
                            style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 12 }}
                          >
                            <span style={{ color: 'rgba(0,0,0,0.65)' }}>
                              {text(c.investors__name)}
                              {c.fund_commitment_statuses__label ? ` · ${c.fund_commitment_statuses__label}` : ''}
                            </span>
                            <span style={{ color: 'rgba(0,0,0,0.45)' }}>
                              {compactMoneyByCode(c.fund_commitments__amount, c.currency_code, currencies)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </List.Item>
                )
              }}
            />
          </Section>
        ) : null
      ) : null}

      <Section title="Contacts">
        {team.length === 0 ? (
          <Typography.Text type="secondary">No contacts.</Typography.Text>
        ) : (
          <List
            size="small"
            dataSource={team}
            rowKey={(c: any) => String(c.contacts__id ?? c.contact_accounts__id)}
            renderItem={(c: any) => {
              const cname = personName(
                c.contacts__first_name,
                c.contacts__last_name,
                c.contacts__preferred_name,
                c.contacts__email
              )
              const roleBits = [c.contact_accounts__title, c.prog__founder ? 'Founder' : null].filter(Boolean).join(' · ')
              const clickable = Boolean(c.contacts__id)
              return (
                <List.Item
                  onClick={clickable ? () => navigate(detailPath('contacts', c.contacts__id)) : undefined}
                  style={{ cursor: clickable ? 'pointer' : 'default', paddingLeft: 4, paddingRight: 4 }}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        size={32}
                        src={c.contacts__photo_url || undefined}
                        style={{ backgroundColor: '#bfbfbf', color: '#fff', fontSize: 12 }}
                      >
                        {avatarInitials(cname)}
                      </Avatar>
                    }
                    title={cname}
                    description={roleBits || undefined}
                  />
                  {clickable ? <FontAwesomeIcon icon={faChevronRight} style={{ color: 'rgba(0,0,0,0.25)' }} /> : null}
                </List.Item>
              )
            }}
          />
        )}
      </Section>

      <Section title="Tasks">
        {tasksState.data === null && tasksState.loading && fallbackTasks.length === 0 ? (
          <SectionSpinner />
        ) : tasksState.data === null && tasksState.error && fallbackTasks.length === 0 ? (
          <SectionError message="Couldn't load tasks." onRetry={tasksState.retry} />
        ) : (
          <TaskList tasks={taskRows} />
        )}
      </Section>

      <Section title="Recent activity">
        {activityState.data === null && activityState.loading ? (
          <SectionSpinner />
        ) : activityState.data === null && activityState.error ? (
          <SectionError message="Couldn't load activity." onRetry={activityState.retry} />
        ) : (
          <InteractionList interactions={activityState.data ?? []} />
        )}
      </Section>
    </div>
  )
}

export default CompanyScreen
